import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { autoinject, observable } from "aurelia-framework";
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { RegexConstants } from '@rightship-tech/common-ui/constants/regex.constants';
import { RightshipInspectionService } from '../../../services/rightship-inspection.service';
import { CountryModel, GetReviseProposalInspectionResponse, InspectionProposalDetailsModel, InspectorModel, RightShipInspectionStatusEnum, SaveInspectionProposalResponse, SubmitProposalStatusEnum, TraineeModel } from '../../../models/rightship-inspections.models';
import { Router } from 'aurelia-router';
import { RsInspectionRouteConstants } from '../../../constants/route.constants';
import { TitleConst } from '../../../constants/title.constaints';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { RsInspectionGoogleAnalyticsActionName } from '../../../constants/google-analytics.constants';
import { CacheClientService, CacheLocation } from '@rightship-tech/common-ui/services/basic/client-caching.service';
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';

@autoinject
export class Proposal {
    public isFreelancerInspector: boolean;
    public isHybridInspection: boolean;
    public estimatedCost: number = 0;
    public inspectors: InspectorModel[] = [];
    public trainee: TraineeModel;
    public showTraineeForm: boolean = false;
    public title: string = TitleConst.SubmitProposal;
    public inspectorsList: InspectorModel[] = [];
    public countriesList: CountryModel[] = [];
    public reviseProposalModel: GetReviseProposalInspectionResponse;
    public proposalDetailModel: InspectionProposalDetailsModel;
    public buttonLabel: string = TitleConst.BtnSubmitProposal;
    public remarks: string;
    public isRightshipInspectionClosed: boolean = false;
    private _encryptedData: string;
    private _validator: ValidationController;
    public isReviseOffer: boolean;
    private _isPreload: boolean = false;
    private _userEmail: string;

    @observable
    public doubleInpector: boolean = false;

    constructor(validationControllerFactory: ValidationControllerFactory, private rightshipInspectionService: RightshipInspectionService, private router: Router,
        private googleAnalyticsService: GoogleAnalyticsService, private cacheClientService: CacheClientService,
        private notificationService: NotificationClientService
    ) {
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }

    async activate(params: { encryptedData: string, reviseOffer: string }) {
        this._encryptedData = decodeURIComponent(params.encryptedData);
        this.isReviseOffer = params.reviseOffer === "true";
        this._userEmail = await this.cacheClientService.get('user-email', CacheLocation.LocalStorage, false);
    }

    async attached() {
        if (this.isReviseOffer) {
            await this.getReviseProposalInspection();
        } else {
            await this.getInspectionProposalDetails();
        }
        this.getProposalDetails();
    }

    async getReviseProposalInspection() {
        try {
            this.title = TitleConst.ReviseProposal;
            this.buttonLabel = TitleConst.BtnReviseProposal;
            this.reviseProposalModel = await this.rightshipInspectionService.getReviseProposalInspection(this._encryptedData);
            if (this.reviseProposalModel?.validationResult?.errors?.length > 0) {
                this.notificationService.show(this.reviseProposalModel.validationResult.errors[0].message, "", NotificationType.Error);
                return;
            }
            this.isRightshipInspectionClosed = this.reviseProposalModel.rightShipInspectionStatusId === RightShipInspectionStatusEnum.Acceptable 
                                                || this.reviseProposalModel.rightShipInspectionStatusId === RightShipInspectionStatusEnum.Unacceptable;
            if (this.reviseProposalModel.isCancelled || this.reviseProposalModel.notFound || this.isRightshipInspectionClosed) {
                this.router.navigateToRoute(RsInspectionRouteConstants.CloseRequest, { 
                    isDeclined: false,
                    encryptedData: encodeURIComponent(this._encryptedData)
                });
                return;
            }
        } catch (error) {
            this.notificationService.show("Error", "Error occur when getting revise proposal inspection details", NotificationType.Error);
        }
    }

    async getInspectionProposalDetails() {
        try {
            this.proposalDetailModel = await this.rightshipInspectionService.getInspectionProposalDetails(this._encryptedData);
            if (this.proposalDetailModel?.validationResult?.errors?.length > 0) {
                this.notificationService.show(this.proposalDetailModel.validationResult.errors[0].message, "", NotificationType.Error);
                return;
            }
            if (this.proposalDetailModel.hasSubmittedProposal || this.proposalDetailModel.isCancelled || this.proposalDetailModel.notFound) {
                this.router.navigateToRoute(RsInspectionRouteConstants.CloseRequest, { 
                    isDeclined: false,
                    hideCloseTab: true,
                    encryptedData: encodeURIComponent(this._encryptedData)
                });
                return;
            }
        } catch (error) {
            this.notificationService.show("Error", "Error occur when getting proposal inspection details", NotificationType.Error);
        }
    }

    getProposalDetails() {
        // If it is a revise offer, then prefill the form with the existing data.
        if (this.isReviseOffer) {
            this.setProposalDetailForReviseOffer();
            this.setDropdownCountries();
            this.preFilledInspectors();
        } else {
            this.setProposalDetailForNewProposal();
            if (this.isFreelancerInspector) {
                const freelancerInspector = this.proposalDetailModel.countryInspectors[0];
                this.inspectors.push({
                    inspectorId: freelancerInspector.requestedInspectors[0].inspectorId,
                    countryId: freelancerInspector.countryId,
                    inspectorName: null,
                    hasConductedLatestInspection: false,
                    showConductedLatestInspectionWarning: false,
                    getSelectableInspectorsList: null
                });
            } else {
                this.setDropdownCountries();
                this.addNewInspector();
            }
        }
    }

    setDropdownCountries() {
        this.countriesList = this.proposalDetailModel.countryInspectors?.map(country => {
            return {
                id: country.countryId,
                name: country.countryName
            };
        });

        if (this.countriesList && this.countriesList.length > 0) {
            this.inspectorsList = this.proposalDetailModel.countryInspectors.flatMap(inspCountry => {
                return inspCountry.requestedInspectors.map(insp => {
                    return {
                        inspectorId: insp.inspectorId,
                        countryId: inspCountry.countryId,
                        inspectorName: insp.inspectorName,
                        hasConductedLatestInspection: insp.hasConductedLatestInspection,
                        showConductedLatestInspectionWarning: false
                    }
                });
            });
        }
    }

    setProposalDetailForReviseOffer() {
        this.proposalDetailModel = {
            vesselDetails: this.reviseProposalModel.vesselDetails,
            portAndOperationalDetails: this.reviseProposalModel.portAndOperationalDetails,
            agentDetails: this.reviseProposalModel.agentDetails,
            countryInspectors: this.reviseProposalModel.countryInspectors,
            hasSubmittedProposal: false,
            isFreelancerInspector: this.reviseProposalModel.isFreelancerInspector,
            isHybridInspection: this.reviseProposalModel.isHybridInspection,
            notFound: false,
            isCancelled: false
        };
        this.estimatedCost = this.reviseProposalModel.previousEstimatedCost;
        this.isFreelancerInspector = this.reviseProposalModel.isFreelancerInspector;
        this.isFreelancerInspector = this.proposalDetailModel.isFreelancerInspector;
        this.isHybridInspection = this.proposalDetailModel.isHybridInspection;
        this.remarks = this.reviseProposalModel.remarks;
    }

    setProposalDetailForNewProposal() {
        this.isFreelancerInspector = this.proposalDetailModel.isFreelancerInspector;
        this.isHybridInspection = this.proposalDetailModel.isHybridInspection;
    }

    preFilledInspectors() {
        if (this.reviseProposalModel.isFreelancerInspector) {
            this.inspectors.push({
                inspectorId: this.reviseProposalModel.selectedInspectors[0].inspectorId,
                countryId: this.reviseProposalModel.selectedInspectors[0].countryId,
                inspectorName: null,
                hasConductedLatestInspection: false,
                showConductedLatestInspectionWarning: false,
                getSelectableInspectorsList: null
            });
        } else {
            if(this.reviseProposalModel.selectedInspectors && this.reviseProposalModel.selectedInspectors.length > 0) {
                this.inspectors.push(...this.reviseProposalModel.selectedInspectors.map(insp => {
                    return {
                        inspectorId: insp.inspectorId,
                        countryId: insp.countryId,
                        inspectorName: null,
                        hasConductedLatestInspection: false,
                        showConductedLatestInspectionWarning: false,
                        getSelectableInspectorsList: (countryId: number) => {
                            if (countryId) {
                                return this.inspectorsList.filter(insp => insp.countryId == countryId);
                            }
                        }
                    }
                }));
            }
            this.trainee = this.reviseProposalModel.selectedTrainee ? {
                name: this.reviseProposalModel.selectedTrainee.name,
                email: this.reviseProposalModel.selectedTrainee.email,
                mobileNumber: this.reviseProposalModel.selectedTrainee.mobileNumber,
                contactCode: this.extractMobileByCode(this.reviseProposalModel.selectedTrainee.mobileNumber).contactCode,
                contactNumber: this.extractMobileByCode(this.reviseProposalModel.selectedTrainee.mobileNumber).contactNumber
            } : null;

            if (this.trainee) {
                this.showTraineeForm = true;
            }

            if (this.inspectors.length == 2) {
                this._isPreload = true;
                this.doubleInpector = true;
            }
        }
    }

    setUpValidation() {
        this.getRules();
    }

    changeInspector(selectedInspector, inspector)
    {
        if (selectedInspector && inspector) {
            inspector.showConductedLatestInspectionWarning = selectedInspector.hasConductedLatestInspection;
        }
    }

    getRules(){
        const emailRegex = new RegExp(RegexConstants.Patterns.EmailRfc5322);

        ValidationRules
            .ensure("estimatedCost")
            .required()
            .satisfies(x => {
                if (typeof x === "string") {
                    x = x.replace(/,/g, '');
                }
                const parseData = Number(x)
                return parseData > 0;
            })
            .withMessage("Please enter cost")
            .on(this);

        this.inspectors.forEach((inspector, index) => {
            ValidationRules
                .ensure('countryId')
                .required()
                .withMessage(`Inspector ${index + 1} country is required`)
                .ensure('inspectorId')
                .satisfies(inspId => inspId != null)
                .withMessage(`Inspector ${index + 1} is required`)
                .satisfies(() => {
                    // check if the selected inspector is not the same as the other inspector and message should appear in the second inspector.
                    if (this.inspectors.length == 2) {
                        if (this.inspectors[0].inspectorId == this.inspectors[1].inspectorId && index == 1) {
                            return false;
                        }
                    }
                    return true;
                })
                .withMessage('Cannot select duplicated inspector. Please select another inspector.')
                .on(inspector);
        });
        
        if (this.trainee) {
            ValidationRules
                .ensure<TraineeModel, any>(x => x.name)
                .required()
                .withMessage("Name is required")
                .ensure(x => x.email)
                .required()
                .withMessage("Email is required")
                .satisfies(email => {
                    return emailRegex.test(email)
                })
                .withMessage("Email is not valid")
                .ensure(x => x.contactCode)
                .required()
                .withMessage("Code is required")
                .ensure(x => x.contactNumber)
                .required()
                .withMessage("Contact number is required")
                .on(this.trainee);
        }
    }

    doubleInpectorChanged(newValue) {
        if (!this._isPreload) {
            if(newValue && this.inspectors && this.inspectors.length > 0) {
                this.addNewInspector();
            }
            else {
                if(this.inspectors && this.inspectors.length > 1){
                    this.inspectors.pop();
                }
            }
        }
        this._isPreload = false;
    }

    private addNewInspector() {
        const inspectorToAdd = {
            inspectorId: null,
            countryId: null,
            inspectorName: null,
            hasConductedLatestInspection: false,
            showConductedLatestInspectionWarning: false,
            getSelectableInspectorsList: (countryId: number) => {
                if (countryId) {
                    return this.inspectorsList.filter(insp => insp.countryId == countryId);
                }
            }
        };

        if (this.countriesList && this.countriesList.length > 0) {
            let defaultSelectedCountryId = this.proposalDetailModel.portAndOperationalDetails.countryId;
            if (defaultSelectedCountryId == null || defaultSelectedCountryId == 0 || this.countriesList.find(x => x.id == defaultSelectedCountryId) == null) {
                defaultSelectedCountryId = this.countriesList[0].id;
            }
            inspectorToAdd.countryId = defaultSelectedCountryId;
            inspectorToAdd.inspectorId = this.inspectorsList.filter(x => x.countryId == defaultSelectedCountryId)[0].inspectorId;
        }
        this.inspectors.push(inspectorToAdd);
    }

    async save() {
        this.setUpValidation();
        const validator = await this._validator.validate();
        if (validator.valid) {
            const request = {
                estimatedCost: this.estimatedCost,
                inspectors: this.inspectors.map(i => {
                    return {
                        inspectorId: i.inspectorId,
                        countryId: i.countryId
                    }
                }),
                trainees: null,
                proposalStatus: SubmitProposalStatusEnum.Submitted,
                remarks: this.remarks
            }
            if (this.trainee) {
                request.trainees = [{
                    email: this.trainee.email,
                    name: this.trainee.name,
                    mobileNumber: this.trainee.contactCode ?  + this.trainee.contactCode + '-' + this.trainee.contactNumber: this.trainee.contactNumber
                }]
            }
            if (this.isReviseOffer) {
                const response = await this.rightshipInspectionService.submitReviseOfferProposalInspection(this._encryptedData, request);
                if (response?.validationResult?.errors?.length > 0) {
                    this.notificationService.show("Submit revise proposal failed!", response.validationResult.errors[0].message, NotificationType.Error);
                    return;
                }

                //Send GA4 event
                if(this._userEmail) {
                    this.googleAnalyticsService.sendCustomGA4EventMultiAnonymously(RsInspectionGoogleAnalyticsActionName.ReviseOffer, {}, this._userEmail);
                } 
                this.navigateToSubmittedProposal(response, this.isReviseOffer);
            } else {
                const response = await this.rightshipInspectionService.saveInspectionProposal(request, this._encryptedData);
                if (response?.validationResult?.errors?.length > 0) {
                    this.notificationService.show("Submit proposal failed!", response.validationResult.errors[0].message, NotificationType.Error);
                    return;
                }
                this.navigateToSubmittedProposal(response);
            }
        }
    }

    navigateToSubmittedProposal(response: SaveInspectionProposalResponse, isReviseOffer: boolean = false) {
        this.router.navigateToRoute(RsInspectionRouteConstants.SubmittedProposal, { 
            vesselName: response.vesselName,
            vesselImo: response.vesselImo, 
            portName: response.port,
            country: response.country,
            requestedInspectionDate: response.requestedInspectionDate,
            isReviseOffer: isReviseOffer,
            hideCloseTab: !isReviseOffer,
            rsInspectionId: response.rsInspectionId,
            encryptedData: encodeURIComponent(this._encryptedData)
        });
    }

    openTraineeForm() {
        this.trainee = {
            name: "",
            email: "",
            mobileNumber: "",
            contactCode: "",
            contactNumber: ""
        }
        this.showTraineeForm = true;
    }

    closeTraineeForm() {
        this.trainee = null;
        this.showTraineeForm = false;
    }

    extractMobileByCode(phoneNumber: string): any {
        const mobile = phoneNumber.split('-');
        return {
            contactCode: mobile[0],
            contactNumber: mobile[1]
        }
    }
}